import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";
import axios from 'axios';
import config from "../configuration/config";
import 'dayjs/locale/ar';
import './MaintenanceCalendar.css';

export const MaintenanceCalendar = ({ selectedDate, onSelect, subServiceID }) => {
    const { t: trans, i18n } = useTranslation(['web']);
    const lang = i18n.language;

    const [viewDate, setViewDate] = useState(dayjs(selectedDate || new Date()));
    const [availableDays, setAvailableDays] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const currentMonth = viewDate.month();

    useEffect(() => {
        fetchDays(currentMonth + 1);
    }, [currentMonth]);

    const fetchDays = async (month) => {
        setIsLoading(true);
        const Body = { "Month": month };
        try {
            const response = await axios.post(`${config.controllURL}${i18n.language || 'ar'}/api/Pages/GetAvailableDaysInMonth`, Body);
            const data = response.data;
            if (Array.isArray(data)) setAvailableDays(data);
            else if (data && Array.isArray(data.data)) setAvailableDays(data.data);
            else setAvailableDays([]);
        } catch (error) {
            console.error("Error fetching available days:", error);
            setAvailableDays([]);
        } finally {
            setIsLoading(false);
        }
    };

    const daysInMonth = viewDate.daysInMonth();
    const firstDayOfMonth = viewDate.startOf('month').day();
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(null);
    }

    const today = dayjs().format('YYYY-MM-DD');

    for (let i = 1; i <= daysInMonth; i++) {
        const dateString = viewDate.date(i).format('YYYY-MM-DD');
        const dayData = (availableDays || []).find(d => {
            if (!d) return false;
            const dDate = d.AvailableDate || d.availableDate || d.Date || d.date;
            return dDate && dayjs(dDate).format('YYYY-MM-DD') === dateString;
        });
        const dType = dayData
            ? (dayData.Type !== undefined ? dayData.Type : (dayData.type !== undefined ? dayData.type : 1))
            : -1;

        days.push({
            day: i,
            date: dateString,
            type: dayData ? parseInt(dType) : -1,
            hours: dayData ? (dayData.AvailableHoursCount || dayData.availableHoursCount || 0) : 0,
            isPast: dayjs(dateString).isBefore(today, 'day')
        });
    }

    const handlePrevMonth = () => setViewDate(viewDate.subtract(1, 'month'));
    const handleNextMonth = () => setViewDate(viewDate.add(1, 'month'));

    const weekDays = lang === 'ar'
        ? ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="maintenance-calendar-wrapper my-8 select-none w-full max-w-full">
            <h4 className="text-2xl md:text-3xl mb-4 font-bold text-gray-700 dark:text-gray-200">
                {trans("cal_select_date")}
            </h4>

            <div className="maintenance-calendar-container overflow-hidden shadow-sm border border-gray-100 dark:border-white/5 rounded-3xl bg-white dark:bg-white/5 p-4 md:p-6 lg:p-8">

                {/* ── Header ── */}
                <div className="calendar-header flex justify-between items-center mb-6 md:mb-8">
                    <button
                        onClick={handlePrevMonth}
                        className="w-16 h-16 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-all active:scale-95"
                    >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            className={`text-primary ${lang === 'ar' ? '' : 'rotate-180'}`}>
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <h3 className="text-2xl md:text-4xl font-black subpixel-antialiased text-primary">
                        {viewDate.locale(lang).format('MMMM YYYY')}
                    </h3>

                    <button
                        onClick={handleNextMonth}
                        className="w-16 h-16 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-all active:scale-95"
                    >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            className={`text-primary ${lang === 'ar' ? 'rotate-180' : ''}`}>
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>

                {/* ── Grid ── */}
                {isLoading ? (
                    <div className="flex flex-col justify-center items-center h-72 space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent shadow-sm"></div>
                        <span className="text-xl font-medium opacity-50">{trans("loading")}</span>
                    </div>
                ) : (
                    <div className="calendar-grid grid grid-cols-7 gap-1 md:gap-2">
                        {/* Week labels */}
                        {weekDays.map((d, i) => (
                            <div key={i} className="text-center font-black text-base sm:text-lg md:text-2xl text-gray-400 opacity-40 py-2 uppercase tracking-tight">
                                {d}
                            </div>
                        ))}

                        {/* Day cells */}
                        {days.map((d, i) => {
                            if (!d) return <div key={`empty-${i}`} className="p-2 md:p-4"></div>;

                            let statusClass = "";
                            let statusIcon = "";
                            let isClickable = false;

                            if (d.type === 1) {
                                statusClass = "day-available";
                                isClickable = true;
                            } else if (d.type === 0) {
                                statusClass = "day-holiday";
                                statusIcon = "fa-sun";
                            } else if (d.type === -1 || d.isPast) {
                                statusClass = "day-unavailable";
                                statusIcon = d.isPast ? "fa-clock-rotate-left opacity-30" : "fa-calendar-xmark";
                            }

                            const isSelected = dayjs(selectedDate).format('YYYY-MM-DD') === d.date;
                            if (isSelected) statusClass += " day-selected";

                            return (
                                <div
                                    key={d.date}
                                    className={`calendar-day ${statusClass} ${isClickable && !isSelected ? 'cursor-pointer hover:scale-105' : ''} ${isSelected ? 'z-10' : ''}`}
                                    onClick={() => isClickable && onSelect(d.date)}
                                >
                                    <span
                                        className={isSelected ? 'mb-1' : ''}
                                        style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', fontWeight: 900, lineHeight: 1 }}
                                    >
                                        {d.day}
                                    </span>
                                    {isSelected && <div className="selection-dot"></div>}
                                    {statusIcon && !isSelected && (
                                        <i className={`fa-solid ${statusIcon} status-icon`}></i>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── Legend ── */}
                <div className="calendar-legend mt-10 p-5 bg-gray-50 dark:bg-white/5 rounded-2xl flex gap-x-8 gap-y-4 flex-wrap text-lg md:text-xl font-semibold">
                    <div className="flex items-center gap-3">
                        <div className="legend-dot bg-available"></div>
                        <span className="opacity-70 text-gray-700 dark:text-gray-300">{trans("cal_available")}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="legend-dot bg-holiday"></div>
                        <span className="opacity-50 text-gray-700 dark:text-gray-300">{trans("cal_holiday")}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="legend-dot bg-unavailable"></div>
                        <span className="opacity-50 text-gray-700 dark:text-gray-300">{trans("cal_unavailable")}</span>
                    </div>
                </div>

            </div>
        </div>
    );
};
